import { useEffect, useState } from 'react';
import { useMedia } from 'react-use';

import { InputLabel, Stack } from '@eluve/components';
import { useNamedLogger } from '@eluve/logger';
import { AudioDeviceOption, getAudioConstraints } from '@eluve/mic-utils';

import { MicrophoneDeviceSelector } from './MicrophoneDeviceSelector';
import { MicrophoneLevel } from './MicrophoneLevel';
import { MicrophoneNoAudioDetected } from './MicrophoneNoAudioDetected';

type AppointmentUserMicSettingsProps = {
  forceVerticalLayout?: boolean;
  isCompact?: boolean;
  selectedMicId: string | null;
  setSelectedMicId: (id: string | null) => void;
  setHasAudioStream?: (hasPreviouslySelectedMicId: boolean) => void;
  showInputLabel?: boolean;
  hasError?: boolean;
};

export const AppointmentUserMicSettings: React.FC<
  AppointmentUserMicSettingsProps
> = ({
  forceVerticalLayout = false,
  isCompact = false,
  selectedMicId,
  setSelectedMicId,
  setHasAudioStream,
  showInputLabel = true,
  hasError,
}: AppointmentUserMicSettingsProps) => {
  const isSmallScreen = !useMedia('(min-width: 32rem)');
  const isVerticalLayout = forceVerticalLayout || isSmallScreen;
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const logger = useNamedLogger('AppointmentUserMicSettings');

  useEffect(() => {
    window.eluveMediaStreams.stop('microphoneTester');

    if (!selectedMicId) {
      setMediaStream(null);
      return;
    }

    if (!navigator.mediaDevices) {
      logger.warn('navigator.mediaDevices is not available');
      setMediaStream(null);
      setHasAudioStream?.(false);
      return;
    }

    navigator.mediaDevices
      .getUserMedia(getAudioConstraints(selectedMicId))
      .then((stream) => {
        logger.info(
          `Successfully started microphone stream for device ${selectedMicId}`,
        );

        setMediaStream(stream);
        window.eluveMediaStreams.set('microphoneTester', stream);
        setHasAudioStream?.(true);
      })
      .catch((error) => {
        logger.warn('Error accessing microphone microphone:', error);
        setMediaStream(null);
        setHasAudioStream?.(false);
      });

    return () => {
      window.eluveMediaStreams.stop('microphoneTester');
    };
  }, [logger, selectedMicId, setHasAudioStream, setSelectedMicId]);

  const handleDeviceChange = (device: AudioDeviceOption | null) => {
    setSelectedMicId(device?.deviceId ?? null);
  };

  const microphoneSelector = (
    <MicrophoneDeviceSelector
      onSelectedDeviceChange={handleDeviceChange}
      selectedId={selectedMicId}
      hasError={hasError}
    />
  );

  return (
    <Stack
      className={
        isCompact && !isVerticalLayout && !hasError
          ? 'flex-row-reverse items-end gap-5'
          : ''
      }
      gap={2}
    >
      {hasError ? (
        <MicrophoneNoAudioDetected />
      ) : (
        <MicrophoneLevel
          stream={mediaStream}
          key={selectedMicId}
          className={isCompact && !isVerticalLayout ? 'w-fit' : 'mb-3 w-full'}
          variant={isCompact || isVerticalLayout ? 'compact' : 'default'}
        />
      )}
      {showInputLabel ? (
        <InputLabel label="Audio Source">{microphoneSelector}</InputLabel>
      ) : (
        microphoneSelector
      )}
    </Stack>
  );
};
